import { WEEKDAYS_LONG } from '@product-framework/pages/ServiceProvider/components/Availability/utils/constants';
import { SnackbarApiErrorMessage } from '@ui-framework/index';

const SUBMISSION_TYPE = {
  UNKNOWN: 'UNKNOWN',
  NON_RECURRING_SINGLE_DAY: 'NON_RECURRING_SINGLE_DAY',
  NON_RECURRING_MULTI_DAY: 'NON_RECURRING_MULTI_DAY',
  RECURRING_DAILY: 'RECURRING_DAILY',
  RECURRING_WEEKLY: 'RECURRING_WEEKLY',
  RECURRING_MONTHLY_DAY_OF_MONTH: 'RECURRING_MONTHLY_DAY_OF_MONTH',
  RECURRING_MONTHLY_ORDINAL_WEEK: 'RECURRING_MONTHLY_ORDINAL_WEEK',
  RECURRING_MONTHLY_ORDINAL_DAY: 'RECURRING_MONTHLY_ORDINAL_DAY',
  RECURRING_YEARLY_MONTH_DAY: 'RECURRING_YEARLY_MONTH_DAY',
  RECURRING_YEARLY_ORDINAL_WEEKDAY_IN_MONTH:
    'RECURRING_YEARLY_ORDINAL_WEEKDAY_IN_MONTH',
};

const SpecialSchedule = {
  async fetch({ params, specialScheduleId = null, locationId = null }) {
    const locationParam = locationId ? `/location/${locationId}` : '';
    const url = `${params.getApiUrl()}/sxcl/v1/availability/service-providers/${params.pageId}${locationParam}/special-schedules/${specialScheduleId}`;
    const response = await params.get(url);
    return response;
  },
  async delete({ params, specialScheduleId, locationId = null }) {
    const locationParam = locationId ? `/location/${locationId}` : '';
    const url = `${params.getApiUrl()}/sxcl/v1/availability/service-providers/${params.pageId}${locationParam}/special-schedules/${specialScheduleId}`;
    const response = await params?.remove(url);
    if (response?.ok) {
      return true;
    }
    params?.snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  },
  async upsert({ params, data, specialScheduleId, locationId = null }) {
    const locationParam = locationId ? `/location/${locationId}` : '';
    const url = `${params.getApiUrl()}/sxcl/v1/availability/service-providers/${params.pageId}${locationParam}/special-schedules/closed${specialScheduleId ? `/${specialScheduleId}` : ''}`;
    const method = specialScheduleId ? params.put : params.post;
    const response = await method(url, data);
    return response;
  },
  isRecurring({ data }) {
    const { recurrenceEnabled } = data;
    return recurrenceEnabled;
  },
  getNonRecurringType({ data }) {
    const { recurrenceFrequency } = data;
    const isSingleDay = recurrenceFrequency === 'SINGLE';
    const isRange = recurrenceFrequency === 'RANGE';
    if (isRange) {
      return SUBMISSION_TYPE.NON_RECURRING_MULTI_DAY;
    }
    if (isSingleDay) {
      return SUBMISSION_TYPE.NON_RECURRING_SINGLE_DAY;
    }
    return SUBMISSION_TYPE.UNKNOWN;
  },
  getRecurringType({ data }) {
    const { recurrenceFrequency, recurrenceMonthlyMode, recurrenceYearlyMode } =
      data;
    switch (recurrenceFrequency) {
      case 'DAILY': {
        return SUBMISSION_TYPE.RECURRING_DAILY;
      }
      case 'WEEKLY': {
        return SUBMISSION_TYPE.RECURRING_WEEKLY;
      }
      case 'MONTHLY': {
        if (recurrenceMonthlyMode === 'DAY_OF_MONTH') {
          return SUBMISSION_TYPE.RECURRING_MONTHLY_DAY_OF_MONTH;
        }
        if (recurrenceMonthlyMode === 'ORDINAL_WEEKDAY') {
          return SUBMISSION_TYPE.RECURRING_MONTHLY_ORDINAL_WEEK;
        }
        if (recurrenceMonthlyMode === 'ORDINAL_DAY') {
          return SUBMISSION_TYPE.RECURRING_MONTHLY_ORDINAL_DAY;
        }
        break;
      }
      case 'YEARLY': {
        if (recurrenceYearlyMode === 'MONTH_DAY') {
          return SUBMISSION_TYPE.RECURRING_YEARLY_MONTH_DAY;
        }
        if (recurrenceYearlyMode === 'ORDINAL_WEEKDAY_IN_MONTH') {
          return SUBMISSION_TYPE.RECURRING_YEARLY_ORDINAL_WEEKDAY_IN_MONTH;
        }
        break;
      }
      default:
        return SUBMISSION_TYPE.UNKNOWN;
    }
    return SUBMISSION_TYPE.UNKNOWN;
  },
  getScheduleSubmissionType({ data }) {
    if (this.isRecurring({ data })) {
      return this.getRecurringType({ data });
    }
    return this.getNonRecurringType({ data });
  },
  getUpsertObject({ data, submissionType }) {
    const slotInfo =
      data?.slotAvailability === 'NOT_AVAILABLE'
        ? { closesAllSlots: true }
        : {
            slots: data?.slots.map(s => ({ slotTypeCode: s.code })),
            closesAllSlots: false,
          };
    const endDate =
      data?.recurringEndDateOptions === 'NO_END_DATE'
        ? { endDate: null }
        : { endDate: data?.endDate };
    switch (submissionType) {
      case SUBMISSION_TYPE.NON_RECURRING_SINGLE_DAY: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          recurrence: {
            enabled: false,
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.NON_RECURRING_MULTI_DAY: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          endDate: data?.endDate,
          recurrence: {
            enabled: false,
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.RECURRING_DAILY: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          ...endDate,
          recurrence: {
            enabled: true,
            frequency: data?.recurrenceFrequency,
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.RECURRING_WEEKLY: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          ...endDate,
          recurrence: {
            enabled: true,
            frequency: data?.recurrenceFrequency,
            weekdays: data?.recurrenceWeekdays?.map(w => w.value),
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.RECURRING_MONTHLY_DAY_OF_MONTH: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          ...endDate,
          recurrence: {
            enabled: true,
            frequency: data?.recurrenceFrequency,
            mode: data?.recurrenceMonthlyMode,
            dayOfMonth: data?.recurrenceMonthlyMode1DOM,
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.RECURRING_MONTHLY_ORDINAL_WEEK: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          ...endDate,
          recurrence: {
            enabled: true,
            frequency: data?.recurrenceFrequency,
            mode: data?.recurrenceMonthlyMode,
            ordinal: data?.recurrenceMonthlyMode2OWO,
            weekday: data?.recurrenceMonthlyMode2OWW,
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.RECURRING_MONTHLY_ORDINAL_DAY: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          ...endDate,
          recurrence: {
            enabled: true,
            frequency: data?.recurrenceFrequency,
            mode: data?.recurrenceMonthlyMode,
            ordinalDay: data?.recurrenceMonthlyMode3O,
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.RECURRING_YEARLY_MONTH_DAY: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          ...endDate,
          recurrence: {
            enabled: true,
            frequency: data?.recurrenceFrequency,
            yearlyMode: data?.recurrenceYearlyMode,
            month: data?.recurrenceYearlyMode1MM,
            yearlyDayOfMonth: data?.recurrenceYearlyMode1MD,
          },
          ...slotInfo,
        };
      }
      case SUBMISSION_TYPE.RECURRING_YEARLY_ORDINAL_WEEKDAY_IN_MONTH: {
        return {
          name: data?.name,
          startDate: data?.startDate,
          ...endDate,
          recurrence: {
            enabled: true,
            yearlyMode: data?.recurrenceYearlyMode,
            frequency: data?.recurrenceFrequency,
            ordinal: data?.recurrenceYearlyMode2OWMO,
            weekday: data?.recurrenceYearlyMode2OWMW,
            month: data?.recurrenceYearlyMode2OWMM,
          },
          ...slotInfo,
        };
      }
      default:
    }
    return null;
  },
  getRecurrenceFrequency({ recurrenceEnabled, recurrenceFrequency, endDate }) {
    if (!recurrenceEnabled && endDate === null) {
      return 'SINGLE';
    }
    if (!recurrenceEnabled && endDate !== null) {
      return 'RANGE';
    }
    if (
      ['DAILY', 'WEEKLY', 'MONTHLY', 'YEARLY'].includes(recurrenceFrequency) &&
      recurrenceEnabled
    ) {
      return recurrenceFrequency;
    }
    return 'UNKNOWN';
  },
  getDefaultValues() {
    const initialValue = {
      name: '',
      startDate: null,
      endDate: null,
      recurrenceEnabled: false,
      recurrenceFrequency: 'SINGLE',
      recurrenceInterval: 1,
      recurrenceWeekdays: [],
      recurrenceYearlyMode: 'MONTH_DAY',
      recurrenceMonthlyMode: 'DAY_OF_MONTH',
      recurrenceYearlyDayOfMonth: null,
      recurringEndDateOptions: 'NO_END_DATE',
      slotAvailability: 'NOT_AVAILABLE',
      recurrenceMonthlyMode1DOM: '',
      recurrenceMonthlyMode2OWO: '',
      recurrenceMonthlyMode2OWW: '',
      recurrenceMonthlyMode3O: '',
      recurrenceYearlyMode1MM: '',
      recurrenceYearlyMode1MD: '',
      recurrenceYearlyMode2OWMO: '',
      recurrenceYearlyMode2OWMW: '',
      recurrenceYearlyMode2OWMM: '',
      slots: [],
    };

    return initialValue;
  },
  getFieldsWithPermission({ readOnly = true }) {
    return {
      name: readOnly ? 'READ_ONLY' : 'WRITE',
      startDate: readOnly ? 'READ_ONLY' : 'WRITE',
      endDate: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceEnabled: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceFrequency: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceInterval: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceWeekdays: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceYearlyMode: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceMonthlyMode: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceYearlyDayOfMonth: readOnly ? 'READ_ONLY' : 'WRITE',
      recurringEndDateOptions: readOnly ? 'READ_ONLY' : 'WRITE',
      slotAvailability: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceMonthlyMode1DOM: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceMonthlyMode2OWO: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceMonthlyMode2OWW: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceMonthlyMode3O: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceYearlyMode1MM: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceYearlyMode1MD: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceYearlyMode2OWMO: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceYearlyMode2OWMW: readOnly ? 'READ_ONLY' : 'WRITE',
      recurrenceYearlyMode2OWMM: readOnly ? 'READ_ONLY' : 'WRITE',
      slots: readOnly ? 'READ_ONLY' : 'WRITE',
    };
  },
  parseSuccessResponseForNextValue({ schedule }) {
    let recurrenceMonthlyMode = 'DAY_OF_MONTH';
    if (
      schedule?.recurrenceConfigJson?.ordinal &&
      schedule?.recurrenceConfigJson?.weekday
    ) {
      recurrenceMonthlyMode = 'ORDINAL_WEEKDAY';
    } else if (schedule?.recurrenceConfigJson?.ordinalDay) {
      recurrenceMonthlyMode = 'ORDINAL_DAY';
    }
    const nextValue = {
      id: schedule?.id,
      name: schedule?.name,
      startDate: schedule?.startDate,
      endDate: schedule?.endDate || null,
      recurrenceEnabled: schedule?.recurrenceEnabled,
      recurrenceFrequency: this.getRecurrenceFrequency({
        recurrenceEnabled: schedule?.recurrenceEnabled,
        endDate: schedule?.endDate || null,
        recurrenceFrequency: schedule?.recurrenceConfigJson?.frequency,
      }),
      recurrenceInterval: schedule?.recurrenceInterval,
      recurringEndDateOptions: !schedule?.endDate ? 'NO_END_DATE' : 'END_BY',
      slotAvailability:
        schedule?.slots?.length > 0
          ? 'NOT_AVAILABLE_FOR_LIMITED_SLOTS'
          : 'NOT_AVAILABLE',
      slots: Array.isArray(schedule?.slots)
        ? schedule?.slots?.map(s => ({
            ...s,
            code: s.slotTypeCode,
          }))
        : [],
      recurrenceWeekdays:
        schedule?.recurrenceConfigJson?.weekdays?.map(x => ({
          label: WEEKDAYS_LONG.find(w => w.value === x).label,
          value: x,
        })) || [],
      recurrenceMonthlyMode,
      recurrenceMonthlyMode1DOM:
        schedule?.recurrenceConfigJson?.dayOfMonth || '',
      recurrenceMonthlyMode2OWO: schedule?.recurrenceConfigJson?.ordinal || '',
      recurrenceMonthlyMode2OWW: schedule?.recurrenceConfigJson?.weekday || '',
      recurrenceMonthlyMode3O: schedule?.recurrenceConfigJson?.ordinalDay || '',

      recurrenceYearlyMode: schedule?.recurrenceConfigJson?.yearlyMode || '',
      recurrenceYearlyMode1MM: schedule?.recurrenceConfigJson?.month || '',
      recurrenceYearlyMode1MD:
        schedule?.recurrenceConfigJson?.yearlyDayOfMonth || '',
      recurrenceYearlyMode2OWMO: schedule?.recurrenceConfigJson?.ordinal || '',
      recurrenceYearlyMode2OWMW: schedule?.recurrenceConfigJson?.weekday || '',
      recurrenceYearlyMode2OWMM: schedule?.recurrenceConfigJson?.month || '',
    };
    return nextValue;
  },
  parseErrorResponseForNextValue({ schedule }) {
    const nextValue = {
      ...schedule,
      recurrenceMonthlyMode1DOM: schedule?.recurrenceMonthlyMode1DOM || '',
      recurrenceMonthlyMode2OWO: schedule?.recurrenceMonthlyMode2OWO || '',
      recurrenceMonthlyMode2OWW: schedule?.recurrenceMonthlyMode2OWW || '',
      recurrenceMonthlyMode3O: schedule?.recurrenceMonthlyMode3O || '',
      recurrenceYearlyMode1MM: schedule?.recurrenceYearlyMode1MM || '',
      recurrenceYearlyMode1MD: schedule?.recurrenceYearlyMode1MD || '',
      recurrenceYearlyMode2OWMO: schedule?.recurrenceYearlyMode2OWMO || '',
      recurrenceYearlyMode2OWMW: schedule?.recurrenceYearlyMode2OWMW || '',
      recurrenceYearlyMode2OWMM: schedule?.recurrenceYearlyMode2OWMM || '',
    };

    return nextValue;
  },
};

export default SpecialSchedule;
